import { useMemo } from 'react';

import { useAssignableUsers } from '@/hooks/leads/useAssignableUsers';
import { useLeadOwners } from '@/hooks/leads/useLeadOwners';
import { useHasPermission } from '@/hooks/usePermissions';
import { formatUserName } from '@/lib/leads/leadFormat';
import { UNASSIGNED_LEAD_FILTER } from '@/types/lead';
import type { AssignableUser } from '@/types/settings';

export interface LeadAssigneeFilterOption {
  value: string;
  label: string;
}

/**
 * Options for the "Assigned to" filter, shared by the split, table and board
 * views so the three cannot drift into offering different rosters — or, worse,
 * different spellings of the unassigned sentinel, which would fail as a 400 on
 * one view and work on another.
 *
 * The roster comes from one of two endpoints, chosen by permission, because
 * filtering by owner is a READ and the assignment roster is not:
 *
 *   - `leads:edit` holders get `assignable-users` — scope-filtered to their
 *     assignment authority, and able to surface a lead's current owner via
 *     `includeUserId` even when that person is deactivated or outside the set.
 *   - Everyone else gets `assignable-owners`, which is served under
 *     `leads:view`. `assignable-users` is gated on `leads:edit`, so the roles
 *     that can read the leads list without editing it — accountant and
 *     dispatcher today — would otherwise 403 on every mount and be left with a
 *     dropdown of nothing but the two sentinels: a filter visible to them and
 *     unusable by them.
 *
 * Deactivated users are kept when `includeUserId` names one. A filter's job is
 * to explain the rows on screen, and leads outlive their owners: drop the
 * departed rep from the list and the leads still stamped with their id become
 * unreachable through the very control that exists to find them. (This applies
 * to the `leads:edit` path only — the read-only roster returns active users,
 * and has no parameter to ask for more.)
 */
export function useLeadAssigneeFilterOptions(options?: {
  includeUserId?: string;
}): LeadAssigneeFilterOption[] {
  // Both filter sentinels have to be screened out, not just the unassigned one:
  // 'all' is equally not a user id, and passing it through asks the server to
  // resolve a member whose id is the literal string "all" — a wasted lookup on
  // every render of an unfiltered list, and a 400 the moment that endpoint
  // validates its input as a UUID.
  const candidate = options?.includeUserId;
  const selectedId =
    candidate && candidate !== UNASSIGNED_LEAD_FILTER && candidate !== 'all'
      ? candidate
      : undefined;

  const canEditLeads = useHasPermission('leads:edit');
  // Exactly one of these fetches; the other stays idle. Leaving both enabled
  // would reintroduce the 403 this branch exists to avoid.
  const assignableQuery = useAssignableUsers({
    enabled: canEditLeads,
    includeUserId: selectedId,
  });
  const ownersQuery = useLeadOwners({ enabled: !canEditLeads });

  const roster = canEditLeads ? assignableQuery.data?.data : ownersQuery.data?.data;

  return useMemo(() => {
    const users = (roster ?? []).filter(
      (user: AssignableUser) => user.status === 'active' || user.id === selectedId,
    );

    return [
      { value: 'all', label: 'All assignees' },
      { value: UNASSIGNED_LEAD_FILTER, label: 'Unassigned' },
      ...users.map((user: AssignableUser) => ({
        value: user.id,
        label: formatUserName(user),
      })),
    ];
  }, [roster, selectedId]);
}
